/**
 * Global Video Manager
 * Ensures only one video plays at a time across all components
 * by pausing/destroying other active videos when a new one starts
 */
class VideoManager {
    constructor() {
        this.activeVideoPlayer = null;
        this.allVideoPlayers = [];
    }

    /**
     * Register a video player
     * @param {object} videoPlayer - The js3q video player instance
     * @param {string} sourceId - Identifier for the source (carousel, overlay, hero, etc)
     */
    registerVideoPlayer(videoPlayer, sourceId) {
        if (!videoPlayer) return;

        // Stop all other videos before starting the new one
        this.stopAllVideos();

        this.activeVideoPlayer = {
            player: videoPlayer,
            sourceId: sourceId,
            timestamp: Date.now()
        };

        // Add to tracking array if not already there
        if (!this.allVideoPlayers.find(v => v.player === videoPlayer)) {
            this.allVideoPlayers.push(this.activeVideoPlayer);
        }

        // Listen for when this video is destroyed
        videoPlayer.once("media.complete", () => {
            this.unregisterVideoPlayer(videoPlayer);
        });
    }

    /**
     * Unregister a video player
     * @param {object} videoPlayer - The js3q video player instance
     */
    unregisterVideoPlayer(videoPlayer) {
        if (this.activeVideoPlayer?.player === videoPlayer) {
            this.activeVideoPlayer = null;
        }
        this.allVideoPlayers = this.allVideoPlayers.filter(
            v => v.player !== videoPlayer
        );
    }

    /**
     * Stop all currently playing videos (pause only, don't destroy)
     */
    stopAllVideos() {
        this.allVideoPlayers.forEach(videoData => {
            try {
                if (
                    videoData.player &&
                    typeof videoData.player.pause === "function"
                ) {
                    videoData.player.pause();
                }
            } catch (error) {
                console.warn("Error pausing video:", error);
            }
        });
        this.activeVideoPlayer = null;
    }

    /**
     * Get the currently active video player
     * @returns {object|null} The active video player data or null
     */
    getActiveVideoPlayer() {
        return this.activeVideoPlayer;
    }

    /**
     * Check if a specific video player is currently active
     * @param {object} videoPlayer - The video player to check
     * @returns {boolean}
     */
    isVideoPlayerActive(videoPlayer) {
        return this.activeVideoPlayer?.player === videoPlayer;
    }
}

// Create and export a singleton instance
export const videoManager = new VideoManager();
